import { INSURANCE_CONSIDERATION_TYPES } from '~/app/modules/insurance/utils/insurance.const';
import { WEB_ROUTES } from '~/utils/webConfig';
import { VERIFICATION_OPTIONS } from '~/utils/consts';

export default async ({ app, route, store, query, softRedirect }) => {
  const {
    CLIENT_BLOCKED,
    APPLICATION_ACCEPTED,
    APPLICATION_COMPLETED,
    APPLICATION_CANCELED,
    APPLICATION_REJECTED,
    APPLICATION_TERMINATED,
    APPLICATION_SPECIAL_OFFER,
    APPLICATION_YHAT_DECREASE,
    APPLICATION_YHAT_INCREASE,
    APPLICATION_MANUAL_VERIFICATION,
    APPLICATION_VERIFICATION,
    APPLICATION_SIGNING,
  } = app.$tools.status;

  const APPLICATION_DELAY_DECISION = 25;

  // Redirect to first step if not logged in
  if (!app.$auth.loggedIn) {
    return softRedirect(app.localePath('registration'));
  }

  await app.$auth.fetchUser();

  const { profile } = app.$client;
  const { info } = profile;
  const [routeName] = (route.name || '').split('___');

  // Redirect to 2nd step if documentNumber are not set
  if (!profile.documentNumber) {
    return softRedirect(app.localePath('registration-step2'));
  }

  // Redirect to 3rd step if employment are not set
  const noEmploymentInfo = Object.keys(info).length && !Object.keys(info.employment).length;
  const noFinanceInfo = !info.finance.additionalIncome.amount;

  if (noEmploymentInfo || noFinanceInfo) {
    return softRedirect(app.localePath('registration-step3'));
  }

  // Redirect to 4th step if wallet or bank are not set
  if (!Object.keys(info.wallet).length && !Object.keys(info.bank.branch).length) {
    return softRedirect(app.localePath('registration-step4'));
  }

  // Get active application
  await store.dispatch('client/getApplication');

  const { application } = app.$client;
  const applicationStatus = application && application.status;
  const applicationConsiderationType = application && application.considerationType;

  if (
    applicationConsiderationType === INSURANCE_CONSIDERATION_TYPES.DRAFT &&
    applicationStatus === APPLICATION_DELAY_DECISION
  ) {
    return softRedirect(app.localePath(WEB_ROUTES.INSURANCE));
  }

  // Redirect to 5th step
  if (
    profile.isIdDocumentAuthenticationRequired &&
    [APPLICATION_VERIFICATION, APPLICATION_DELAY_DECISION].includes(applicationStatus)
  ) {
    return softRedirect(app.localePath('registration-step5'));
  }

  if (profile && profile.showRolloverOtpForm) {
    return softRedirect(app.localePath(WEB_ROUTES.ROLLOVER_OTP));
  }

  if (
    !profile.socialFundConsent.canBeSkipped &&
    [APPLICATION_VERIFICATION, APPLICATION_DELAY_DECISION].includes(applicationStatus)
  ) {
    return softRedirect(app.localePath('social-fund'));
  }

  if (applicationStatus === APPLICATION_SIGNING) {
    if (application.verificationType === VERIFICATION_OPTIONS.VIDEO) {
      return softRedirect(app.localePath(WEB_ROUTES.VIDEO_CAPTURE));
    }

    if (application.verificationType === VERIFICATION_OPTIONS.BRANCH) {
      return softRedirect(app.localePath(WEB_ROUTES.MANUAL_VERIFICATION));
    }

    return softRedirect(app.localePath(WEB_ROUTES.SELECT_VERIFICATION));
  }

  // Logout blacklisted client
  if (profile.status === CLIENT_BLOCKED) {
    return app.$auth.logout();
  }

  // Stay at error page if called intentionally
  if (routeName === 'application-rejected' && query.f) {
    return softRedirect(app.localePath('application-rejected'));
  }

  // Redirect to rejected page if application was rejected
  if (applicationStatus === APPLICATION_REJECTED && application.canNotMakeApplication) {
    return softRedirect(app.localePath('application-rejected'));
  }

  // Redirect to success page if application was accepted
  if (applicationStatus === APPLICATION_ACCEPTED) {
    return softRedirect(app.localePath('application-accepted'));
  }

  if (
    applicationStatus === APPLICATION_DELAY_DECISION &&
    applicationConsiderationType === INSURANCE_CONSIDERATION_TYPES.WITHOUT_INSURANCE
  ) {
    return softRedirect(app.localePath(WEB_ROUTES.INSURANCE_DELAY_DECISION));
  }

  if ([APPLICATION_SPECIAL_OFFER, APPLICATION_YHAT_DECREASE].includes(applicationStatus)) {
    return softRedirect(app.localePath('application-offer-decrease'));
  }

  if (applicationStatus === APPLICATION_YHAT_INCREASE) {
    return softRedirect(app.localePath('application-offer-increase'));
  }

  if (applicationStatus === APPLICATION_MANUAL_VERIFICATION) {
    if (application.verificationType === VERIFICATION_OPTIONS.VIDEO) {
      return profile.videoUploadRequired
        ? softRedirect(app.localePath(WEB_ROUTES.VIDEO_CAPTURE))
        : softRedirect(app.localePath(WEB_ROUTES.APPLICATION_LOADING_VIDEO));
    }

    return softRedirect(app.localePath(WEB_ROUTES.MANUAL_VERIFICATION));
  }

  if (applicationStatus === APPLICATION_VERIFICATION) {
    if (application.verificationType === VERIFICATION_OPTIONS.VIDEO) {
      return softRedirect(app.localePath(WEB_ROUTES.VIDEO_CAPTURE));
    }

    if (application.verificationType === VERIFICATION_OPTIONS.BRANCH) {
      return softRedirect(app.localePath(WEB_ROUTES.MANUAL_VERIFICATION));
    }

    return softRedirect(app.localePath(WEB_ROUTES.APPLICATION_LOADING));
  }

  // Redirect to loading page if application is processing
  if (
    applicationStatus &&
    ![
      APPLICATION_COMPLETED,
      APPLICATION_REJECTED,
      APPLICATION_CANCELED,
      APPLICATION_TERMINATED,
    ].includes(applicationStatus)
  ) {
    return softRedirect(app.localePath('application-loading'));
  }

  if (routeName === 'application-apply') {
    return true;
  }

  if (!profile.hasActiveLoan) {
    return softRedirect(app.localePath('profile-no-loan'));
  }

  await store.dispatch('client/getLoan');
  await store.dispatch('client/getActivePaymentSchedule');
  return softRedirect(app.localePath('profile-my-loan'));
};
